import { Component } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import type { NavItem } from './sidebar-menu.model';
import { NgTemplateOutlet } from '@angular/common';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '../../auth/auth.service';
import {UserRolesEnum} from '../../auth/user_roles.enum';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
  standalone: true,
  imports: [NgTemplateOutlet, RouterModule, NgbModule]
})
export class SidebarComponent {
  // Which groups are expanded
  open = new Set<string>();

  private readonly allMenu: NavItem[] = [
    {
      id: 'welcome',
      label: 'Welcome',
      icon: 'bi-house',
      route: '/welcome',
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: 'bi-gear',
      children: [
        {
          id: 'counties',
          label: 'Countries',
          icon: 'bi-globe-americas',
          route: '/settings/countries'
        },
        {
          id: 'warehouses',
          label: 'Warehouses',
          icon: 'bi-send',
          route: '/settings/warehouses'
        },
        {
          id: 'brands',
          label: 'Brands',
          icon: 'bi-bing',
          route: '/settings/brands'
        },
        {
          id: 'wooStore',
          label: 'Woo Store',
          icon: 'bi-shop',
          route: '/settings/woo-store'
        },
      ]
    },
    {
      id: 'products',
      label: 'Products',
      icon: 'bi-hexagon-fill',
      children: [
        {
          id: 'attributes',
          label: 'Attributes',
          icon: 'bi-bounding-box',
          route: '/products/attributes'
        },
        {
          id: 'products',
          label: 'Products',
          icon: 'bi-box-seam',
          route: '/products/master-products'
        },
        {
          id: 'taxonomies',
          label: 'Taxonomies',
          icon: 'bi-tag',
          route: '/products/taxonomies',
          //roles: [UserRolesEnum.ADMIN]
        },
      ]
    },
  ];

  menu: NavItem[] = [];

  constructor(private router: Router, private authService: AuthService) {}

  toggle(id: string) {
    if (this.open.has(id)) this.open.delete(id);
    else this.open.add(id);
  }

  isOpen(id: string) {
    return this.open.has(id);
  }

  // Optional: auto-open groups that contain the current route
  ngOnInit() {
    this.authService.user$.subscribe(() => {
      this.menu = this.filterMenu(this.allMenu);
      this.expandParentsForCurrentRoute();
    });
  }

  private filterMenu(items: NavItem[]): NavItem[] {
    if (!this.authService.isLoggedIn()) return [];

    const userRoles = this.authService.user?.roles ?? [];
    const isSuperAdmin = userRoles.includes(UserRolesEnum.SUPER_ADMIN);

    return items.reduce<NavItem[]>((acc, item) => {
      // super_admin sees everything; no roles restriction means visible to all logged-in users
      if (!isSuperAdmin && item.roles?.length && !item.roles.some(r => userRoles.includes(r as any))) {
        return acc;
      }

      const filtered = { ...item };
      if (filtered.children?.length) {
        filtered.children = this.filterMenu(filtered.children);
        // hide parent if all children were filtered out
        if (!filtered.children.length) return acc;
      }

      acc.push(filtered);
      return acc;
    }, []);
  }

  private expandParentsForCurrentRoute() {
    const url = this.router.url;
    const walk = (items: NavItem[], parents: string[] = []) => {
      for (const item of items) {
        const nextParents = item.children?.length ? [...parents, item.id] : parents;
        if (item.route && url.startsWith(item.route)) {
          parents.forEach((p) => this.open.add(p));
        }
        if (item.children?.length) walk(item.children, nextParents);
      }
    };
    walk(this.menu, []);
  }
}
